import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UnitsProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Units } from "assets/SideBarIcons/units.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import Loader from "layout/Loader";
import { GetUnitByIdResponse } from "subModule/src/services/units/interface";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
interface UnitProps {
  setAddNew?: any;
  refetch?: any;
}
export default function UnitModal(props: UnitProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedUnitNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phUnitNameAr") : t("phUnitNameEn");
  const [loading, setLoading] = useState<boolean>(
    params?.id && !props?.setAddNew ? true : false,
  );
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id && !props?.setAddNew ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "general",
    moduleLabelKey: "units",
    moduleHref: "/units",
    entityFallbackLabelKey: "unitTitle",
    isEdit: !!params?.id && !props?.setAddNew,
  });
  useEffect(() => {
    if (params?.id && !props?.setAddNew) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    UnitsProfileService.getUnitById(+(params as any)?.id)
      .then((res: GetUnitByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}), isDefault: res?.data?.default });
          captureLabel(res?.data?.unitTitle);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      delete data.default;
      UnitsProfileService.updateUnits(+(params as any)?.id, { ...data })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.unitTitle);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("unit") + " " + t("updated") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
          setIsBtnEnabled(true);
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      UnitsProfileService.createUnits(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  (props?.setAddNew ? t("baseUnitId") : t("unit")) +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            if (props?.setAddNew) {
              props?.setAddNew("");
              props.refetch();
            } else navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      {props?.setAddNew ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={true}
          onClick={() => navigate(-1)}
          onSubmit={handleSubmit(onSubmit)}
          heading={
            params?.id && !props?.setAddNew ? t("editUnits") : t("addUnits")
          }
          icon={<Units height={28} width={28} />}
          btnText={"back"}
          btnType={"back"}
          isBtnEnabled={IsBtnEnabled}
          showCreateBtn={checkPrivileges("ROLE_UNIT_CREATE")}
          breadcrumbItems={breadcrumbItems}
        />
      )}
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id && !props?.setAddNew ? "editUnits" : "addUnits"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id && !props?.setAddNew ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          entityPopup={props?.setAddNew}
          padding={!props?.setAddNew}
          content={
            <Grid
              container
              component="form"
              id="form"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("unitTitle", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                  placeholder={localizedUnitNamePlaceholder(primaryLanguage)}
                  title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.unitTitle}
                  helperText={errors.unitTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("secondaryUnitTitle")}
                  label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                  placeholder={localizedUnitNamePlaceholder(secondaryLanguage)}
                  title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.secondaryUnitTitle}
                  helperText={errors.secondaryUnitTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  //   {...register("unitCode")}
                  {...register("unitCode", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={t("unitCode") + " *"}
                  placeholder={t("phUnitCode")}
                  title={t("unitCode")}
                  fullWidth
                  error={!!errors.unitCode}
                  helperText={errors.unitCode?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  );
}
